import { useState, useEffect } from 'react'

import { Grid, Typography, makeStyles, IconButton, Container, Button } from '@material-ui/core'

import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded'
import moment from 'moment'

import useTranslate from '../i18n/useTranslate'
import ClosePanel from '../component/report/closed/Panel'
import closedExport from '../component/report/closed/report'
import { useSearchClosed, ClosedKeywordType, ClosedDataListType } from '../services/um/useSearchClosed'
import useAuthenStore from '../stores/authen/authenStore'
import SearchAccordion from '../control/accordion/SearchAccordion'
import { convertDateToFormatTH, convertDateToFormatEN } from '../utilities/date'
import { setPrefixName } from './../utilities/prefixName'
import Table from '../component/report/closed/Table'
import ArrowBackRounded from '@material-ui/icons/ArrowBackRounded'
import { useHistory } from 'react-router'
import { useQueryParam } from 'use-query-params'
import useProjectType from '../services/um/lookups/useProjectType'

const ClosedForm = ({}) => {
    const translate = useTranslate()
    const lang = translate.languageStore.currentLanguage as 'th' | 'en'
    const classes = useStyles()
    const history = useHistory()

    const { searchClosed } = useSearchClosed()
    const [dataList, setDataList] = useState<ClosedDataListType[]>([])
    const authenStore = useAuthenStore()
    const { projectT } = useProjectType()
    const [keyword, setKeyword] = useQueryParam<string | undefined>('keyword')
    const [projectType, setProjectType] = useQueryParam<number | undefined | null>('projectType')
    const [startDate, setStartDate] = useQueryParam<Date | string>('startDate')
    const [endDate, setEndDate] = useQueryParam<Date | string>('endDate')

    const setSearchParam = (value: ClosedKeywordType) => {
        setKeyword(value.keyword)
        setProjectType(value.projectType)
        setStartDate(moment(value.startDate).format('YYYY-MM-DD') ?? '')
        setEndDate(moment(value.endDate).format('YYYY-MM-DD') ?? '')
    }
    const [initialValue, setInitialValue] = useState<ClosedKeywordType>({
        keyword: keyword,
        projectType: projectType,
        startDate: startDate
            ? moment(startDate).format('YYYY-MM-DD')
            : moment().subtract(1, 'year').format('YYYY-MM-DD'),
        endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
        userID: authenStore.profile.userId ?? 0,
    })

    useEffect(() => {
        if (authenStore.profile.userId) {
            onSubmit(initialValue)
        }
    }, [authenStore.profile.userId])

    const onSubmit = async (value: ClosedKeywordType) => {
        setInitialValue(value)
        const data = await searchClosed({
            keyword: value.keyword,
            projectType: value.projectType ? Number(value.projectType) : null,
            startDate: value.startDate,
            endDate: value.endDate,
            userID: authenStore.profile.userId ?? 0,
        })
        setDataList(data)
        setSearchParam(value)
    }

    const onClear = async (form: any) => {
        form.reset()
        setInitialValue({
            startDate: moment().subtract(1, 'year').format('YYYY-MM-DD'),
            endDate: moment().format('YYYY-MM-DD'),
            userID: authenStore.profile.userId ?? 0,
        })
    }
    const getProjectTypeName = (value?: number | null) => {
        let result = null
        if (value) {
            result = projectT.find((item) => item.value === projectType?.toString())?.label
        }
        return result ?? '-'
    }

    const handleExportExcel = () => {
        closedExport({
            header: {
                projectType: getProjectTypeName(projectType),
                startDate: convertDate(moment(startDate).format('YYYY-MM-DD')),
                endDate: convertDate(moment(endDate).format('YYYY-MM-DD')),
                keyword: keyword ?? '-',
            },
            content: dataList.map((item) => {
                return {
                    projectID: item.projectNumber,
                    receiveDate: convertDate(item.receiveDate),
                    endDate: convertDate(item.closedDate),
                    researcherName: setPrefixName(item)[lang] ?? '-',
                    position: item.researcherTypeName[lang] ?? '-',
                    projectNameTH: item.projectName['th'] ?? '-',
                    projectNameEN: item.projectName['en'] ?? '-',
                    projectType: item.projectType.name[lang] ?? '-',
                }
            }),
            translate: (value: string) => translate.get(value),
        })
    }

    const convertDate = (date: string | null) => {
        if (lang === 'th' && date) {
            return convertDateToFormatTH(date)
        } else if (lang === 'en' && date) {
            return convertDateToFormatEN(date)
        } else {
            return '-'
        }
    }

    const onBack = () => {
        history.push('/dashboard')
    }

    return (
        <Container maxWidth="lg">
            <Grid className={classes.root}>
                <SearchAccordion title={translate.get('UM.CLOSED.HEADER')} isBack={true} onClickBack={onBack}>
                    <ClosePanel onSubmit={onSubmit} initialValue={initialValue} onClear={onClear} />
                </SearchAccordion>
                <Grid container justify="space-between" alignItems="center" className={classes.container}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6">
                            {translate.get('LABEL.SEARCH_TOTAL_RESULT', [`${dataList.length}`])}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Grid container justify="flex-end" spacing={2}>
                            <Grid item>
                                <Button
                                    size="large"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<GetAppRoundedIcon style={{ color: '#DB5F8E' }} />}
                                    onClick={handleExportExcel}
                                >
                                    <Typography variant="button">{translate.get('BUTTON.EXPORT')}</Typography>
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Table dataList={dataList} />
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    )
}

const useStyles = makeStyles(() => ({
    root: {
        width: '100%',
    },
    container: {
        margin: '15px 0px',
        width: '100%',
    },
}))

export default ClosedForm
